import React from "react";
import { Eye } from "lucide-react";
import {
  DAY_OPTIONS,
  FY_INVESTMENT_MONTHS,
  WINDOW_KINDS,
  normalizeAudienceFlags,
} from "../../utils/investmentDeclarationWindows";

/**
 * Shared FY month windows grid (Enable Investment / Proof).
 * kind: "investment" | "proof"
 */
export default function InvestmentMonthWindowsGrid({
  kind = "investment",
  darkMode = false,
  windows,
  onUpdateMonth,
  onOpenSelection,
  onViewSelected,
  selectionTargetMonth = null,
  selectCls = "",
  /** When true, entire grid is view-only (e.g. Flexi mirroring Declaration). */
  locked = false,
}) {
  const meta = WINDOW_KINDS[kind] || WINDOW_KINDS.investment;
  const primaryFlag = meta.primaryFlag;
  const primaryLabel = meta.primaryLabel;

  return (
    <div
      className={`border rounded-none overflow-x-auto ${
        darkMode ? "border-gray-600" : "border-gray-300"
      }`}
    >
      <div className="min-w-[920px]">
        <div
          className={`grid grid-cols-[76px_52px_68px_68px_118px_108px_96px_40px] gap-x-2 px-3 py-2 text-xs font-semibold uppercase tracking-wide items-center sticky top-0 z-10 ${
            darkMode
              ? "bg-gray-800 text-gray-400"
              : "bg-gray-100 text-gray-600"
          }`}
        >
          <span>Month</span>
          <span className="text-center">Enable</span>
          <span>From day</span>
          <span>To day</span>
          <span>{primaryLabel}</span>
          <span>Enable all</span>
          <span>Selection</span>
          <span className="sr-only">View</span>
        </div>
        <div className="max-h-56 overflow-y-auto">
          {FY_INVESTMENT_MONTHS.map(({ num, label }) => {
            const cfg = windows?.months?.[num] || {
              enabled: false,
              fromDay: 1,
              toDay: 31,
              [primaryFlag]: false,
              allowAll: true,
              allowSelection: false,
              selectedEmployeeIds: [],
            };
            const flags = normalizeAudienceFlags(cfg, null, kind);
            const fromDay = Number(cfg.fromDay) || 1;
            const toDay = Math.max(Number(cfg.toDay) || fromDay, fromDay);
            const selectingThisMonth = selectionTargetMonth === num;
            const selectedCount = (cfg.selectedEmployeeIds || []).length;
            const rowDisabled = locked || !cfg.enabled;

            return (
              <div
                key={num}
                className={`grid grid-cols-[76px_52px_68px_68px_118px_108px_96px_40px] gap-x-2 items-center px-3 py-2 border-t whitespace-nowrap ${
                  darkMode ? "border-gray-600" : "border-gray-200"
                }`}
              >
                <span
                  className={`text-sm font-medium truncate ${
                    darkMode ? "text-gray-200" : "text-gray-800"
                  }`}
                >
                  {label}
                </span>
                <div className="flex justify-center">
                  <input
                    type="checkbox"
                    checked={!!cfg.enabled}
                    disabled={locked}
                    onChange={(e) =>
                      onUpdateMonth(num, {
                        enabled: e.target.checked,
                        ...(e.target.checked
                          ? {}
                          : {
                              [primaryFlag]: false,
                              allowAll: true,
                              allowSelection: false,
                              selectedEmployeeIds: [],
                            }),
                      })
                    }
                    className="rounded-none"
                    aria-label={`Enable ${label}`}
                  />
                </div>
                <select
                  value={fromDay}
                  disabled={rowDisabled}
                  onChange={(e) =>
                    onUpdateMonth(num, { fromDay: Number(e.target.value) })
                  }
                  className={`${selectCls} w-full`}
                  aria-label={`${label} from day`}
                >
                  {DAY_OPTIONS.map((day) => (
                    <option key={day} value={day}>
                      {day}
                    </option>
                  ))}
                </select>
                <select
                  value={toDay}
                  disabled={rowDisabled}
                  onChange={(e) =>
                    onUpdateMonth(num, { toDay: Number(e.target.value) })
                  }
                  className={`${selectCls} w-full`}
                  aria-label={`${label} to day`}
                >
                  {DAY_OPTIONS.filter((day) => day >= fromDay).map((day) => (
                    <option key={day} value={day}>
                      {day}
                    </option>
                  ))}
                </select>

                <label
                  className={`inline-flex items-center gap-1.5 cursor-pointer ${
                    rowDisabled ? "opacity-50 pointer-events-none" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={!!flags[primaryFlag]}
                    disabled={rowDisabled}
                    onChange={(e) =>
                      onUpdateMonth(num, {
                        [primaryFlag]: e.target.checked,
                      })
                    }
                    className="rounded-none"
                  />
                  <span
                    className={`text-sm ${
                      darkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    {primaryLabel}
                  </span>
                </label>

                <label
                  className={`inline-flex items-center gap-1.5 cursor-pointer ${
                    rowDisabled ? "opacity-50 pointer-events-none" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={!!flags.allowAll}
                    disabled={rowDisabled}
                    onChange={(e) =>
                      onUpdateMonth(num, { allowAll: e.target.checked })
                    }
                    className="rounded-none"
                  />
                  <span
                    className={`text-sm ${
                      darkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Enable all
                  </span>
                </label>

                <label
                  className={`inline-flex items-center gap-1.5 cursor-pointer ${
                    rowDisabled ? "opacity-50 pointer-events-none" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={!!flags.allowSelection || selectingThisMonth}
                    disabled={rowDisabled}
                    onChange={(e) => {
                      if (e.target.checked) {
                        onUpdateMonth(num, { allowSelection: true });
                        onOpenSelection?.(num);
                      } else {
                        onUpdateMonth(num, {
                          allowSelection: false,
                          selectedEmployeeIds: [],
                        });
                      }
                    }}
                    className="rounded-none"
                  />
                  <span
                    className={`text-sm ${
                      darkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Selection
                  </span>
                </label>

                <div className="flex justify-center">
                  {(flags.allowSelection || selectingThisMonth) && (
                    <button
                      type="button"
                      title={
                        selectedCount
                          ? `View ${selectedCount} selected employee(s)`
                          : "No employees selected yet"
                      }
                      onClick={() => {
                        if (selectedCount > 0) {
                          onViewSelected?.(num);
                        } else {
                          onOpenSelection?.(num);
                        }
                      }}
                      className={`inline-flex items-center justify-center p-1 bg-transparent border-0 shadow-none outline-none rounded-none ${
                        selectedCount
                          ? "text-redhalo-red hover:opacity-80"
                          : darkMode
                            ? "text-gray-500 hover:text-gray-300"
                            : "text-gray-400 hover:text-gray-600"
                      }`}
                    >
                      <Eye size={16} strokeWidth={2} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
